import { BusinessOutlined } from '@mui/icons-material';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Brand, Brand_Enum } from '@kheops/graphql';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import DialogCloseButton from '../../common/components/DialogCloseButton';
import CompanyLogo from '../../common/components/CompanyLogo';
import { useInsertRoleAssignmentBrandMutation } from '../../mutations/__generated__/insertRoleAssignmentBrand.generated';
import { UserRoleDocument } from '../../queries/__generated__/userRole.generated';
import { currentContextAtom } from '../../state';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import SelectableList from '../../common/components/SelectableList';
import { SettingsDialogViewProps } from './SettingsDialog';

export interface BrandsSettingsProps extends SettingsDialogViewProps {
  brands: Pick<Brand, 'value' | 'comment'>[];
  currentBrands?: Pick<Brand, 'value' | 'comment'>[];
}

export default function BrandsSetting({
  next,
  standAlone,
  submitLabel,
  brands,
  currentBrands,
}: BrandsSettingsProps): React.JSX.Element {
  const { t } = useTranslation(['settings', 'common']);
  const context = useAtomValue(currentContextAtom);
  const [selectedBrands, setSelectedBrands] = useState<Pick<Brand, 'value' | 'comment'>[]>(currentBrands || []);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [insertRoleAssignmentBrand, { data, loading }] = useInsertRoleAssignmentBrandMutation({
    refetchQueries: [UserRoleDocument],
    awaitRefetchQueries: true,
  });

  const isAllSelected = selectedBrands.length === brands?.length;

  const handleSubmit = (): void => {
    insertRoleAssignmentBrand({
      variables: {
        role_assignment_id: context.roleAssignmentId,
        roleAssignmentBrands: !isAllSelected
          ? selectedBrands.map((brand) => {
            return {
              role_assignment_id: context.roleAssignmentId,
              brand: brand.value as Brand_Enum,
            };
          })
          : {
            role_assignment_id: context.roleAssignmentId,
            has_all_brands: true,
          },
      },
    });
  };

  useEffect((): void => {
    if (data?.insert_role_assignment_brand?.affected_rows
      || data?.delete_role_assignment_brand?.affected_rows) {
      setCommonSnackbarProps({
        label: t('settings:your_profile_has_been_updated'),
        snackbarProps: {
          open: true,
        },
      });

      next({ skipped: !!standAlone });
    }
  }, [data]);

  const isValidationDisabled = useMemo(() => {
    if (currentBrands && selectedBrands.length !== currentBrands?.length) {
      return false;
    }

    const selectedValues = selectedBrands.map((brand) => brand.value).sort();
    const currentValues = (currentBrands ?? []).map((brand) => brand.value).sort();

    return selectedValues.every((v, i) => v === currentValues[i]);
  }, [selectedBrands]);

  return (
    <>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex'
          }}
        >
          <BusinessOutlined />
          <Typography sx={{ flex: 1 }}>
            {t('settings:your_brands')}
          </Typography>
        </Box>
        <Typography>{t('settings:your_brands_description')}</Typography>
      </DialogTitle>
      {standAlone && (
        <DialogCloseButton onClick={() => next()} />
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          py: 0,
          gap: 1.5,

        }}
      >
        <SelectableList
          items={brands}
          selected={selectedBrands}
          getKey={(brand) => brand.value}
          getLabel={(brand) => brand.comment!}
          onChange={setSelectedBrands}
          renderIcon={(b) => (
            <CompanyLogo size="medium" variant="transparent" brand={b.value as Brand_Enum} sx={{ width: 48 }} />
          )}
        />
      </DialogContent>
      <DialogActions>
        {standAlone
          ? (
            <Button onClick={() => next()} variant="outlined">
              {t('common:cancel')}
            </Button>
          )
          : (
            <Button onClick={() => next({ skipped: true })} variant="outlined">
              {t('settings:complete_later')}
            </Button>
          )}
        <Button
          disabled={isValidationDisabled}
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
        >
          {submitLabel || t('common:validate')}
        </Button>
      </DialogActions>
    </>
  );
}
